/**
 * WebAuthn utilities for biometric authentication
 * Uses platform authenticators (FaceID, TouchID, fingerprint) to secure vault credentials
 *
 * Flow:
 * 1. Create credential - generates a key pair, private key secured by biometrics
 * 2. Authenticate - biometric verification returns authenticatorData
 * 3. Use authenticatorData to derive encryption key for master password
 */

export interface WebAuthnCredential {
  credentialId: string;
  rawId: ArrayBuffer;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Check if WebAuthn is supported in this browser
 */
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    typeof navigator.credentials.create === 'function' &&
    typeof navigator.credentials.get === 'function'
  );
}

export interface BiometricDiagnostics {
  webAuthnSupported: boolean;
  platformAuthenticatorAvailable: boolean | null;
  error: string | null;
}

/**
 * Get detailed diagnostics about biometric availability
 */
export async function getBiometricDiagnostics(): Promise<BiometricDiagnostics> {
  const result: BiometricDiagnostics = {
    webAuthnSupported: isWebAuthnSupported(),
    platformAuthenticatorAvailable: null,
    error: null,
  };

  if (!result.webAuthnSupported) {
    return result;
  }

  try {
    result.platformAuthenticatorAvailable =
      await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (e) {
    result.error = e instanceof Error ? e.message : String(e);
  }

  return result;
}

/**
 * Check if platform authenticator (FaceID/TouchID/fingerprint) is available
 * Returns true only if biometric authentication is possible
 */
export async function isBiometricAvailable(): Promise<boolean> {
  const diag = await getBiometricDiagnostics();
  return diag.platformAuthenticatorAvailable === true;
}

/**
 * Create a new biometric credential for vault unlock
 * This is called during setup - user authenticates with biometrics to create the credential
 *
 * @param userId - User identifier (usually email)
 * @param userName - Display name for the credential
 * @returns Credential ID that should be stored for future authentication
 */
export async function createBiometricCredential(
  userId: string,
  userName: string
): Promise<WebAuthnCredential> {
  // Generate a random challenge (not validated server-side for our use case)
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const credential = (await navigator.credentials.create({
    publicKey: {
      // Challenge is required but not verified (local-only use)
      challenge,

      // Relying party info
      rp: {
        name: 'DSC Vault',
        id: window.location.hostname,
      },

      // User info
      user: {
        id: new TextEncoder().encode(userId),
        name: userName,
        displayName: userName,
      },

      // Supported algorithms (ES256 preferred, RS256 fallback)
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' }, // ES256 (ECDSA P-256)
        { alg: -257, type: 'public-key' }, // RS256 (RSA)
      ],

      // Authenticator requirements
      authenticatorSelection: {
        // Only platform authenticators (FaceID, TouchID, Windows Hello)
        authenticatorAttachment: 'platform',
        // Require user verification (biometric/PIN)
        userVerification: 'required',
        // Prefer discoverable credential (passkey)
        residentKey: 'preferred',
      },

      // Timeout in milliseconds
      timeout: 60000,

      // Attestation not needed for local use
      attestation: 'none',
    },
  })) as PublicKeyCredential;

  if (!credential) {
    throw new Error('Failed to create credential');
  }

  return {
    credentialId: arrayBufferToBase64(credential.rawId),
    rawId: credential.rawId,
  };
}

/**
 * Authenticate using biometrics
 * Returns authenticatorData which can be used to derive a decryption key
 *
 * @param credentialId - The credential ID from createBiometricCredential
 * @returns authenticatorData from the assertion
 */
export async function authenticateWithBiometric(
  credentialId: string
): Promise<ArrayBuffer> {
  // Generate a random challenge
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge,

      // Only allow our specific credential
      allowCredentials: [
        {
          id: base64ToArrayBuffer(credentialId),
          type: 'public-key',
          transports: ['internal'], // Platform authenticator only
        },
      ],

      // Require user verification (biometric)
      userVerification: 'required',

      // Timeout
      timeout: 60000,

      // Use the same RP ID as during creation
      rpId: window.location.hostname,
    },
  })) as PublicKeyCredential;

  if (!assertion) {
    throw new Error('Authentication failed');
  }

  const response = assertion.response as AuthenticatorAssertionResponse;

  // Return authenticatorData - this is deterministic for the same authenticator
  // and can be used as input for key derivation
  return response.authenticatorData;
}

/**
 * Delete a biometric credential
 * Note: WebAuthn doesn't have a standard way to delete credentials from the authenticator.
 * This function is a placeholder - in practice, we just remove our stored data.
 *
 * On some platforms, users can manage passkeys in system settings:
 * - iOS: Settings > Passwords
 * - Android: Settings > Passwords & accounts > Google > Passkeys
 * - macOS: System Preferences > Passwords
 */
export function deleteBiometricCredential(_credentialId: string): void {
  // No-op: WebAuthn doesn't support credential deletion from JavaScript
  // User must manage credentials via system settings if needed
}
